import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, createVNode as _createVNode, withCtx as _withCtx } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "dashboard-loading"
}
const _hoisted_2 = { class: "loading-text" }
const _hoisted_3 = {
  key: 1,
  class: "project-layout"
}
const _hoisted_4 = { class: "project-content" }
const _hoisted_5 = { class: "project-main-content" }
const _hoisted_6 = {
  key: 0,
  class: "no-project-selected"
}
const _hoisted_7 = { class: "no-project-title" }
const _hoisted_8 = { class: "no-project-message" }
const _hoisted_9 = { key: 1 }
const _hoisted_10 = { key: 0 }
const _hoisted_11 = { key: 1 }
const _hoisted_12 = { key: 2 }

import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from '@/composables/useStore'
import { useNotifications } from '@/composables/useNotifications'
import { useAuthStore } from '@/stores'
import type { Project } from '@/types'

// Components
import AppLayout from '../base/AppLayout.vue'
import QuickActionsTabs from './QuickActionsTabs.vue'
import ProjectService from '../../services/project.service'
import { ProjectInfo, DynamicContent, ProjectAuditTrail } from './index'

// Composables
interface QuickActionTab {
  name: string
  label: string
  title: string
  icon: string
  permission?: string | string[]
  permissions?: string[]
  priority?: 'primary' | 'secondary'
  category?: string
  badge?: string | number
}

// Load project data

export default /*@__PURE__*/_defineComponent({
  __name: 'ProjectDetails',
  setup(__props) {

/// <reference types="vue" />
// @ts-ignore
const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const store = useStore()
const authStore = useAuthStore()
const { notifySuccess, notifyError } = useNotifications()

// Determine if the current user is a CLIENT
const isClientUser = computed(() => authStore.user?.type === 'CLIENT')

// Reactive state
const loading = ref<boolean>(false)
const selectedProject = ref<Project | null>(null)
const originalValues = ref<Project>({} as Project)
const activeComponent = ref<string | null>(null)

// Types for quick action tabs
const loadProjects = async (): Promise<void> => {
  try {
    loading.value = true
    const projectId = route.params.projectId as string

    if (projectId) {
      const projectData = await ProjectService.getProjectById(projectId)

      if (!projectData) {
        throw new Error('Project data is empty or null')
      }

      selectedProject.value = projectData
      originalValues.value = { ...projectData } // Store original values after loading

      // Update store with current project data for breadcrumb
      store.commit('SET_CURRENT_PROJECT', projectData)

      // Only set to 'info' if no other component is already active or requested via route
      if (!activeComponent.value && !route.query.component) {
        setActiveComponent('info')
      }
    } else {
      // Handle the case where no projectId is provided
      selectedProject.value = null
      originalValues.value = {} as Project

      // Clear current project from store
      store.commit('CLEAR_CURRENT_PROJECT')
    }
  } catch (error) {
    notifyError(t('projects.errorLoadingProject'))
    selectedProject.value = null

    // Clear current project from store on error
    store.commit('CLEAR_CURRENT_PROJECT')
  } finally {
    loading.value = false
  }
}

// Lifecycle hooks
onMounted(() => {
  if (route.params.projectId) {
    loadProjects().then(() => {
      // Check if there's a component to activate from the query parameter
      const componentFromQuery = route.query.component as string
      if (componentFromQuery) {
        setActiveComponent(componentFromQuery)
      } else {
        // Set 'info' as the default active tab when a project is loaded
        setActiveComponent('info')
      }
    })
  }
})

// Watch for route query changes to update active component
watch(
  () => route.query.component,
  (newComponent: string | string[] | undefined) => {
    if (newComponent && typeof newComponent === 'string') {
      activeComponent.value = newComponent
    }
  }
)

// Clean up store when component is unmounted
onUnmounted(() => {
  // Clear current project from store when leaving the component
  store.commit('CLEAR_CURRENT_PROJECT')
})

// Set the active component and update the URL
const setActiveComponent = (componentName: string): void => {
  activeComponent.value = componentName
  
  // Update URL query parameter without reloading the page
  if (route.query.component !== componentName) {
    router.push({
      name: 'ProjectDetails',
      params: { projectId: route.params.projectId },
      query: { component: componentName }
    })
  }
}

// Navigate to the project info tab
const returnToProject = (): void => {
  setActiveComponent('info')
}

// Handle retry for dynamic content
const handleDynamicContentRetry = (): void => {
  // Implementation would depend on the specific component
  // Retry loading content for the active component
}

// Update project data
const updateProject = (newProjectData: Project): void => {
  selectedProject.value = { ...newProjectData }
}

// Handle project saved event from ProjectInfo component
const handleProjectSaved = (savedProject: Project): void => {
  originalValues.value = { ...savedProject }
  notifySuccess(t('projects.changesSaved'))
}

// Make quickActionTabs reactive to language changes
const quickActionTabs = computed((): QuickActionTab[] => {
  const baseTabs: QuickActionTab[] = [
    {
      name: 'info',
      label: t('projects.projectInfo'),
      title: t('projects.projectInfo'),
      icon: `<span style="font-size: 1.2em;">ℹ️</span>`,
      permission: 'VIEW_PROJECT_INFO',
      priority: 'primary'
    },
    {
      name: 'planning',
      label: t('projects.planningAction'),
      title: t('projects.planningAction'),
      icon: `<span style="font-size: 1.2em;">📅</span>`,
      permissions: ['ACCESS_PHASES', 'ACCESS_TASKS'],
      priority: 'primary',
      category: 'management'
    },
    {
      name: 'photos',
      label: t('projects.photosAction'),
      title: t('projects.photosAction'),
      icon: `<span style="font-size: 1.2em;">📷</span>`,
      permission: 'ACCESS_PHOTO',
      priority: 'primary'
    },
    {
      name: 'documents',
      label: t('projects.documentsAction'),
      title: t('projects.documentsAction'),
      icon: `<span style="font-size: 1.2em;">📄</span>`,
      permission: 'ACCESS_DOCUMENT',
      priority: 'primary'
    },
    {
      name: 'workspace',
      label: t('projects.workspaceAction'),
      title: t('projects.workspaceAction'),
      icon: `<span style="font-size: 1.2em;">🏗️</span>`,
      permissions: ['ACCESS_PHASES', 'ACCESS_TASKS', 'ACCESS_INTERVENANTS'],
      priority: 'primary',
      category: 'management'
    },
    {
      name: 'budget',
      label: t('projects.budgetAction'),
      title: t('projects.budgetAction'),
      icon: `<span style="font-size: 1.2em;">💰</span>`,
      permission: 'ACCESS_FINANCE',
      priority: 'primary',
      category: 'finance'
    },
    {
    name: 'access',
      label: t('projects.accessAction'),
      title: t('projects.accessAction'),
      icon: `<span style="font-size: 1.2em;">🔒</span>`,
      permission: 'MANAGE_PROJECT_ACCESS',
      priority: 'secondary',
      category: 'settings'
    },
    {
      name: 'design',
      label: t('projects.designAction'),
      title: t('projects.designAction'),
      icon: `<span style="font-size: 1.2em;">📐</span>`,
      permission: 'ACCESS_3D_DESIGN',
      priority: 'secondary',
      category: 'design'
    },
    {
      name: 'notes',
      label: t('projects.notesAction'),
      title: t('projects.notesAction'),
      icon: `<span style="font-size: 1.2em;">📝</span>`,
      permission: 'NOTES',
      priority: 'secondary',
      category: 'communication'
    },
    {
      name: 'report',
      label: t('projects.viewReport'),
      title: t('projects.viewReport'),
      icon: `<span style="font-size: 1.2em;">📈</span>`,
      permission: 'VIEW_REPORTS',
      priority: 'secondary',
      category: 'reporting'
    },
    {
      name: 'issues',
      label: t('projects.issuesAction'),
      title: t('projects.issuesAction'),
      icon: `<span style="font-size: 1.2em;">⚠️</span>`,
      permission: 'ACCESS_ISSUES',
      priority: 'secondary',
      category: 'delivery'
    },
    {
      name: 'notifications',
      label: t('projects.notificationsAction'),
      title: t('projects.notificationsAction'),
      icon: `<span style="font-size: 1.2em;">🔔</span>`,
      priority: 'secondary',
      category: 'notifications'
    },
    {
      name: 'audit',
      label: t('projects.auditTrail'),
      title: t('projects.auditTrail'),
      icon: `<span style="font-size: 1.2em;">📋</span>`,
      permission: 'VIEW_PROJECT_AUDIT',
      priority: 'secondary',
      category: 'audit'
    }
  ]

  // In FULL_MANAGEMENT projects the client must not see finance or reports
  const isFullManagement = selectedProject.value?.managementType === 'FULL_MANAGEMENT'
  if (isClientUser.value && isFullManagement) {
    return baseTabs.filter(tab => tab.name !== 'budget' && tab.name !== 'report')
  }

  return baseTabs
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(AppLayout, null, {
    default: _withCtx(() => [
      (loading.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
            _cache[0] || (_cache[0] = _createElementVNode("div", { class: "loading-spinner-large" }, null, -1)),
            _createElementVNode("p", _hoisted_2, _toDisplayString(_unref(t)('common.loading')), 1)
          ]))
        : (_openBlock(), _createElementBlock("div", _hoisted_3, [
            _createElementVNode("div", _hoisted_4, [
              (selectedProject.value && selectedProject.value.id !== undefined && selectedProject.value.id !== null)
                ? (_openBlock(), _createBlock(QuickActionsTabs, {
                    key: 0,
                    tabs: quickActionTabs.value,
                    activeTab: activeComponent.value,
                    projectId: selectedProject.value.id,
                    maxPrimaryTabs: 5,
                    enableGrouping: true,
                    onSelect: setActiveComponent
                  }, null, 8, ["tabs", "activeTab", "projectId"]))
                : _createCommentVNode("", true),
              _createElementVNode("div", _hoisted_5, [
                (!selectedProject.value)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                      _cache[1] || (_cache[1] = _createElementVNode("div", { class: "no-project-illustration" }, [
                        _createElementVNode("svg", {
                          width: "120",
                          height: "120",
                          viewBox: "0 0 24 24",
                          fill: "none",
                          xmlns: "http://www.w3.org/2000/svg"
                        }, [
                          _createElementVNode("path", {
                            d: "M21 19V5C21 3.89543 20.1046 3 19 3H5C3.89543 3 3 3.89543 3 5V19C3 20.1046 3.89543 21 5 21H19C20.1046 21 21 20.1046 21 19Z",
                            stroke: "var(--neutral-300)",
                            "stroke-width": "2"
                          }),
                          _createElementVNode("path", {
                            d: "M9 9H15",
                            stroke: "var(--neutral-300)",
                            "stroke-width": "2",
                            "stroke-linecap": "round"
                          }),
                          _createElementVNode("path", {
                            d: "M9 13H15",
                            stroke: "var(--neutral-300)",
                            "stroke-width": "2",
                            "stroke-linecap": "round"
                          }),
                          _createElementVNode("path", {
                            d: "M9 17H13",
                            stroke: "var(--neutral-300)",
                            "stroke-width": "2",
                            "stroke-linecap": "round"
                          })
                        ])
                      ], -1)),
                      _createElementVNode("h3", _hoisted_7, _toDisplayString(_unref(t)('projects.noProjectTitle')), 1),
                      _createElementVNode("p", _hoisted_8, _toDisplayString(_unref(t)('projects.selectProject')), 1)
                    ]))
                  : (_openBlock(), _createElementBlock("div", _hoisted_9, [
                      (activeComponent.value === 'info')
                        ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                            _createVNode(_unref(ProjectInfo), {
                              project: selectedProject.value,
                              "original-project": originalValues.value,
                              "onUpdate:project": updateProject,
                              onSaved: handleProjectSaved
                            }, null, 8, ["project", "original-project"])
                          ]))
                        : (activeComponent.value)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
                              _createVNode(_unref(DynamicContent), {
                                "component-name": activeComponent.value,
                                "project-id": selectedProject.value?.id,
                                project: selectedProject.value,
                                onRetry: handleDynamicContentRetry,
                                onReturnToProject: returnToProject
                              }, null, 8, ["component-name", "project-id", "project"])
                            ]))
                          : (_openBlock(), _createElementBlock("div", _hoisted_12, [
                              _createVNode(_unref(ProjectInfo), {
                                project: selectedProject.value,
                                "original-project": originalValues.value,
                                "onUpdate:project": updateProject,
                                onSaved: handleProjectSaved
                              }, null, 8, ["project", "original-project"])
                            ]))
                    ]))
              ])
            ])
          ]))
    ]),
    _: 1
  }))
}
}

})